"use client";

import { useState } from "react";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { routes } from "@/lib/routes";
import { site } from "@/content/site";
import type { ReservationInput } from "@/lib/whatsapp";
import { formatReservationDate } from "@/lib/reservation-date";
import { SectionHead } from "@/components/sections/section-head";
import { ReservationCard } from "@/components/sections/reservation-card";
import { Button } from "@/components/ui/button";

export function PrenotaFlow() {
  const locale = useLocale();
  const t = useTranslations("prenota");
  const tConfirm = useTranslations("confirmation");
  const [booking, setBooking] = useState<ReservationInput | null>(null);

  if (booking) {
    return (
      <div className="py-24 text-center md:py-32">
        <Image
          src="/brand/logo.png"
          alt=""
          width={979}
          height={779}
          sizes="120px"
          className="mx-auto mb-8 h-auto w-28"
        />
        <h1 className="font-display text-[56px] leading-[1.05] font-medium tracking-[-0.015em]">
          {tConfirm("title")}
          <br />
          <em className="text-rosso-pozzo font-normal not-italic">{tConfirm("titleEm")}</em>
        </h1>
        <p className="mx-auto mt-4 max-w-[48ch] text-[17px] text-pietra">{tConfirm("text")}</p>

        <dl className="mx-auto mt-9 grid max-w-[460px] grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 rounded-sm border border-border bg-bianco-carta p-7 text-left">
          <dt className="eyebrow self-center">{tConfirm("name")}</dt>
          <dd className="font-display text-[19px]">{booking.name || "—"}</dd>
          <dt className="eyebrow self-center">{tConfirm("date")}</dt>
          <dd className="font-display text-[19px]">
            {formatReservationDate(booking.date, locale === "en" ? "en" : "it")}
          </dd>
          <dt className="eyebrow self-center">{tConfirm("time")}</dt>
          <dd className="font-display text-[19px]">{booking.time}</dd>
          <dt className="eyebrow self-center">{tConfirm("guests")}</dt>
          <dd className="font-display text-[19px]">{booking.guests}</dd>
        </dl>

        <Button variant="ghost" className="mt-10" nativeButton={false} render={<Link href={routes.home} />}>
          {tConfirm("back")}
        </Button>
      </div>
    );
  }

  return (
    <div className="py-14 md:py-20">
      <SectionHead
        eyebrow={t("eyebrow")}
        title={
          <>
            {t("title")}
            <br />
            <em className="font-normal not-italic">{t("titleEm")}</em>
          </>
        }
      />
      <ReservationCard standalone onSubmit={setBooking} />
      <p className="mx-auto mt-8 max-w-[560px] text-center font-display text-[17px] text-pietra italic">
        {t("groupNote", { phone: site.phoneDisplay })}
      </p>
    </div>
  );
}
